import React, { useState } from 'react';

// ClosedPositionCard Component
const ClosedPositionCard = ({ position, onExpand, isExpanded }) => {
  if (!position) return null;

  const {
    symbol,
    isLong,
    side,
    qty,
    entryPrice,
    exitPrice,
    unrealizedPL: rawUnrealizedPL,
    target,
    stop,
    exitType: rawExitType,
    holdTimeMin,
    entryTime,
    exitTime,
    tax,
    isWin: rawIsWin,
    rrRatio
  } = position;

  const positionSide = isLong !== undefined ? (isLong ? 'LONG' : 'SHORT') : (side === 'buy' ? 'LONG' : 'SHORT');

  // CRITICAL FIX: Recalculate P&L correctly for SHORT positions
  // The backend value might be inverted for shorts
  let unrealizedPL = rawUnrealizedPL;
  let isWin = rawIsWin;
  
  if (entryPrice && exitPrice && qty) {
    if (positionSide === 'LONG') {
      // LONG: profit when exit > entry
      unrealizedPL = (exitPrice - entryPrice) * qty;
    } else {
      // SHORT: profit when entry > exit (covered at lower price)
      unrealizedPL = (entryPrice - exitPrice) * qty;
    }
    
    // Recalculate win/loss based on corrected P&L
    isWin = unrealizedPL > 0;
  }

  // CRITICAL FIX: Validate exitType based on actual prices and position direction
  let exitType = rawExitType;
  
  if (entryPrice && exitPrice && target && stop) {
    const entryFloat = parseFloat(entryPrice);
    const exitFloat = parseFloat(exitPrice);
    const targetFloat = parseFloat(target);
    const stopFloat = parseFloat(stop);
    
    if (positionSide === 'LONG') {
      // LONG: Target is above entry, Stop is below entry
      const hitTarget = Math.abs(exitFloat - targetFloat) < 0.02; // Within 2 cents of target
      const hitStop = Math.abs(exitFloat - stopFloat) < 0.02; // Within 2 cents of stop
      
      if (hitTarget) {
        exitType = 'Target Fill';
      } else if (hitStop) {
        exitType = 'Stop Hit';
      } else if (exitFloat > entryFloat) {
        // Closed at profit but not at target = partial profit/market close
        exitType = rawExitType === 'Market Close' ? 'Market Close' : 'Target Fill';
      } else if (exitFloat < entryFloat) {
        // Closed at loss = likely stop or manual close
        exitType = 'Stop Hit';
      }
    } else {
      // SHORT: Target is below entry, Stop is above entry
      const hitTarget = Math.abs(exitFloat - targetFloat) < 0.02;
      const hitStop = Math.abs(exitFloat - stopFloat) < 0.02;
      
      if (hitTarget) {
        exitType = 'Target Fill';
      } else if (hitStop) {
        exitType = 'Stop Hit';
      } else if (exitFloat < entryFloat) {
        // Covered lower = profit
        exitType = rawExitType === 'Market Close' ? 'Market Close' : 'Target Fill';
      } else if (exitFloat > entryFloat) {
        // Covered higher = loss = stop hit
        exitType = 'Stop Hit';
      }
    }
  }

  // Calculate net P&L (after fees)
  const netPL = unrealizedPL - (Math.abs(tax) || 0);

  // CRITICAL FIX: Recalculate hold time from entry/exit timestamps
  let displayHoldTime = holdTimeMin ? `${holdTimeMin}min` : '0min';
  
  if (entryTime && exitTime) {
    try {
      const entryDate = new Date(entryTime);
      const exitDate = new Date(exitTime);
      const holdTimeMs = exitDate - entryDate;
      const calculatedMinutes = Math.round(holdTimeMs / 60000);
      
      if (calculatedMinutes > 60) {
        const hours = Math.floor(calculatedMinutes / 60);
        const minutes = calculatedMinutes % 60;
        displayHoldTime = `${hours}h ${minutes}m`;
      } else if (calculatedMinutes > 0) {
        displayHoldTime = `${calculatedMinutes}min`;
      } else {
        displayHoldTime = '< 1min';
      }
    } catch (e) {
      // Fallback to original if date parsing fails
      displayHoldTime = holdTimeMin ? `${holdTimeMin}min` : '0min';
    }
  }

  // Format exit type with emoji
  const getExitTypeDisplay = (type) => {
    switch(type) {
      case 'Target Fill': return '🎯 Target Fill';
      case 'Stop Hit': return '⛔ Stop Hit';
      case 'Market Close': return '🟡 Market Close';
      case 'Cover': return '🔄 Cover';
      default: return type || 'Unknown';
    }
  };

  const handleCardClick = () => {
    if (onExpand) {
      onExpand(position);
    }
  };

  return (
    <div className={`closed-position-card ${isWin ? 'win' : 'loss'}`} onClick={handleCardClick}>
      <style>{`
        .closed-position-card {
          background: linear-gradient(135deg, #1a1a1a 0%, #161616 100%);
          border-radius: 8px;
          padding: 12px;
          border: 1px solid #2a2a2a;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 8px;
        }

        .closed-position-card:hover {
          border-color: #3a3a3a;
          background: linear-gradient(135deg, #1e1e1e 0%, #1a1a1a 100%);
          transform: translateX(4px);
        }

        .closed-position-card.win {
          border-left: 3px solid #10b981;
        }

        .closed-position-card.loss {
          border-left: 3px solid #ef4444;
        }

        .closed-position-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }

        .closed-position-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .closed-side-badge {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 700;
          color: white;
        }

        .closed-side-badge.long {
          background: #10b981;
        }

        .closed-side-badge.short {
          background: #ef4444;
        }

        .closed-symbol {
          font-size: 14px;
          font-weight: 700;
          color: #e0e0e0;
        }

        .exit-type-badge {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 9px;
          font-weight: 600;
          background: rgba(255, 255, 255, 0.1);
          color: #999;
        }

        .closed-pnl {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .closed-pnl-amount {
          font-size: 15px;
          font-weight: 700;
        }

        .closed-pnl-amount.win {
          color: #10b981 !important;
        }

        .closed-pnl-amount.loss {
          color: #ef4444 !important;
        }

        .closed-pnl-net {
          font-size: 10px;
          color: #888;
        }

        .closed-position-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 11px;
          color: #888;
        }

        .price-flow {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .price-value {
          color: #aaa;
          font-weight: 600;
        }

        .price-arrow {
          color: #666;
        }

        .duration-badge {
          padding: 2px 6px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
          color: #888;
        }

        .closed-position-expanded {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #2a2a2a;
        }

        .detail-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
          font-size: 11px;
        }

        .detail-label {
          color: #888;
        }

        .detail-value {
          color: #e0e0e0;
          font-weight: 600;
        }

        .detail-value.positive {
          color: #10b981 !important;
        }

        .detail-value.negative {
          color: #ef4444 !important;
        }

        .net-pnl-row {
          padding-top: 8px;
          border-top: 1px solid #2a2a2a;
          font-weight: 700;
        }
      `}</style>

      <div className="closed-position-header">
        <div className="closed-position-title">
          <span className={`closed-side-badge ${positionSide.toLowerCase()}`}>
            {positionSide === 'LONG' ? 'L' : 'S'}
          </span>
          <span className="closed-symbol">{symbol}</span>
          <span className="exit-type-badge">{getExitTypeDisplay(exitType)}</span>
        </div>
        <div className="closed-pnl">
        <span className={`closed-pnl-amount ${isWin ? 'win' : 'loss'}`} 
      style={{ color: isWin ? '#10b981' : '#ef4444' }}>
  {isWin ? '+' : '-'}${Math.abs(unrealizedPL).toFixed(2)}
</span>
          {tax !== 0 && (
            <span className="closed-pnl-net">
              Net: {netPL >= 0 ? '+' : ''}${netPL.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      <div className="closed-position-details">
        <div className="price-flow">
          <span className="price-value">${entryPrice.toFixed(2)}</span>
          <span className="price-arrow">→</span>
          <span className="price-value">${exitPrice.toFixed(2)}</span>
        </div>
        <span className="duration-badge">{displayHoldTime}</span>
      </div>

      {isExpanded && (
        <div className="closed-position-expanded">
          <div className="detail-row">
            <span className="detail-label">Quantity:</span>
            <span className="detail-value">{qty}</span>
          </div>
          
          {target > 0 && (
            <div className="detail-row">
              <span className="detail-label">Target:</span>
              <span className="detail-value positive">${target.toFixed(2)}</span>
            </div>
          )}
          
          {stop > 0 && (
            <div className="detail-row">
              <span className="detail-label">Stop Loss:</span>
              <span className="detail-value negative">${stop.toFixed(2)}</span>
            </div>
          )}
          
          {rrRatio !== '—' && rrRatio > 0 && (
            <div className="detail-row">
              <span className="detail-label">R:R Ratio:</span>
              <span className="detail-value">{rrRatio}:1</span>
            </div>
          )}
          
          {tax !== 0 && (
            <div className="detail-row">
              <span className="detail-label">Fees:</span>
              <span className="detail-value negative">
                {tax < 0 ? '' : '-'}${Math.abs(tax).toFixed(2)}
              </span>
            </div>
          )}
          
          <div className="detail-row net-pnl-row">
            <span className="detail-label">Net P&L:</span>
            <span className={`detail-value ${netPL >= 0 ? 'positive' : 'negative'}`}
      style={{ color: netPL >= 0 ? '#10b981' : '#ef4444' }}>
  {netPL >= 0 ? '+' : '-'}${Math.abs(netPL).toFixed(2)}
</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Exit Time:</span>
            <span className="detail-value">
              {new Date(exitTime).toLocaleTimeString()}
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Entry Time:</span>
            <span className="detail-value">
              {new Date(entryTime).toLocaleTimeString()}
            </span>
          </div>
          
          <div className="detail-row">
            <span className="detail-label">Duration:</span>
            <span className="detail-value">{displayHoldTime}</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ClosedPositions Container Component
const ClosedPositions = ({ positions, totalRealized, tradeCount }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Filter to only closed positions (open_qty === 0)
  const closedPositions = positions ? positions.filter(p => {
    const openQty = p.open_qty || 0;
    return openQty === 0 && p.exitTime && p.exitPrice;
  }) : [];

  // Calculate summary stats
  const winningTrades = closedPositions.filter(p => p.isWin).length;
  const losingTrades = closedPositions.filter(p => !p.isWin).length;
  const winRate = closedPositions.length > 0 
    ? ((winningTrades / closedPositions.length) * 100).toFixed(0)
    : 0;

  // Calculate total realized P&L from positions if not provided
  const calculatedRealized = closedPositions.reduce((sum, p) => {
    return sum + (parseFloat(p.unrealizedPL) || 0);
  }, 0);

  const displayRealized = totalRealized !== undefined ? totalRealized : calculatedRealized;
  const displayCount = tradeCount !== undefined ? tradeCount : closedPositions.length;

  const handleCardExpand = (position) => {
    const positionId = position.id || position.a_id || position.group;
    setExpandedCardId(expandedCardId === positionId ? null : positionId);
  };

  return (
    <div className={`closed-positions-drawer ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <style>{`
        .closed-positions-drawer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: #1a1a1a;
          border-top: 2px solid #333;
          transition: height 0.3s ease;
          z-index: 1000;
        }

        .closed-positions-drawer.collapsed {
          height: 50px;
        }

        .closed-positions-drawer.expanded {
          height: 400px;
        }

        .drawer-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          cursor: pointer;
          user-select: none;
          height: 50px;
          border-bottom: 1px solid #2a2a2a;
        }

        .drawer-header:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .drawer-title {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .toggle-icon {
          font-size: 12px;
          color: #888;
          transition: transform 0.3s ease;
        }

        .drawer-title h3 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #e0e0e0;
          letter-spacing: 0.5px;
        }

        .drawer-stats {
          display: flex;
          align-items: center;
          gap: 16px;
          font-size: 13px;
        }

        .stat {
          color: #888;
        }

        .stat strong {
          color: #e0e0e0;
          margin-left: 4px;
        }

        .stat strong.positive {
          color: #10b981;
        }

        .stat strong.negative {
          color: #ef4444;
        }

        .stat-divider {
          color: #444;
        }

        .win-rate-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 600;
          font-size: 12px;
        }

        .win-rate-badge.good {
          background: rgba(16, 185, 129, 0.15);
          color: #10b981;
        }

        .win-rate-badge.average {
          background: rgba(251, 191, 36, 0.15);
          color: #fbbf24;
        }

        .win-rate-badge.poor {
          background: rgba(239, 68, 68, 0.15);
          color: #ef4444;
        }

        .drawer-content {
          height: calc(100% - 50px);
          overflow-y: auto;
          padding: 16px 20px;
        }

        .closed-positions-list {
          display: flex;
          flex-direction: column;
        }

        .drawer-content::-webkit-scrollbar {
          width: 6px;
        }

        .drawer-content::-webkit-scrollbar-track {
          background: transparent;
        }

        .drawer-content::-webkit-scrollbar-thumb {
          background: rgba(255, 255, 255, 0.2);
          border-radius: 3px;
        }

        .drawer-content::-webkit-scrollbar-thumb:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .empty-closed-state {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .empty-closed-state-icon {
          font-size: 48px;
          margin-bottom: 12px;
          opacity: 0.5;
        }

        .empty-closed-state-text {
          font-size: 14px;
          color: #888;
        }
      `}</style>

      {/* Drawer Header (always visible) */}
      <div 
        className="drawer-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="drawer-title">
          <span className="toggle-icon">{isExpanded ? '▼' : '▲'}</span>
          <h3>CLOSED POSITIONS (Today)</h3>
        </div>
        <div className="drawer-stats">
          <span className="stat">
            Realized: <strong className={displayRealized >= 0 ? 'positive' : 'negative'}>
              {displayRealized >= 0 ? '+' : ''}${displayRealized.toFixed(2)}
            </strong>
          </span>
          <span className="stat-divider">|</span>
          <span className="stat">{displayCount} Trades</span>
          <span className="stat-divider">|</span>
          <span className={`win-rate-badge ${
            winRate >= 60 ? 'good' : winRate >= 45 ? 'average' : 'poor'
          }`}>
            {winRate}% Win Rate
          </span>
          <span className="stat-divider">|</span>
          <span className="stat">
            W: <strong className="positive">{winningTrades}</strong> / 
            L: <strong className="negative">{losingTrades}</strong>
          </span>
        </div>
      </div>

      {/* Drawer Content (expandable) */}
      {isExpanded && (
        <div className="drawer-content">
          {closedPositions.length > 0 ? (
            <div className="closed-positions-list">
              {closedPositions.map((position, index) => (
                <ClosedPositionCard
                  key={position.id || position.a_id || position.group || index}
                  position={position}
                  onExpand={handleCardExpand}
                  isExpanded={expandedCardId === (position.id || position.a_id || position.group)}
                />
              ))}
            </div>
          ) : (
            <div className="empty-closed-state">
              <div className="empty-closed-state-icon">📋</div>
              <div className="empty-closed-state-text">No closed positions today</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ClosedPositions;